import { useMemo, useState } from 'react';

import type { BatchDraftsItem } from 'api';
import { useGetDisbursementPayoutBatchesBatchIdPayoutDrafts } from 'api';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';
import { useHandleRowClick } from 'hooks/useHandleRowClick';
import { useVirtualList } from 'hooks/useVirtualList';

import { DataTable } from 'components/ui/organisms/DataTable';

import { BatchPayoutDraftDetails } from '../BatchPayoutDraftDetails';
import { getColumnsDraft } from '../columns';
import { BATCH_PAYOUTS_DRAFTS_TABLE_TEST_ID } from '../constants';
import { useLocalState } from '../localState';

export const BatchDraftPayoutsList = ({ batchId }: { batchId: string }) => {
  const organisationId = useOrganisationId<number>();

  const [selectedRowData, setSelectedRowData] = useState<BatchDraftsItem>();

  const { findAccountByAccountNumber } = useVirtualAccounts();

  const batchPayoutsColumns = useMemo(
    () => getColumnsDraft(findAccountByAccountNumber),
    [findAccountByAccountNumber]
  );

  const { updateCursorAfter: setCursorAfter, state: localState } =
    useLocalState();

  const { data: payoutDrafts, isFetching } =
    useGetDisbursementPayoutBatchesBatchIdPayoutDrafts(
      organisationId,
      Number(batchId),
      { 'page[cursor_after]': localState.cursorAfter }
    );

  const { onScroll, data } = useVirtualList<BatchDraftsItem>({
    data: payoutDrafts?.data,
    uniqueKey: 'id',
    isFetching,
    initialRefValue: payoutDrafts?.meta?.cursor_next,
    updateCursorCallback: setCursorAfter,
    resetConditions: [organisationId],
    cursorNext: payoutDrafts?.meta?.has_next_page
      ? payoutDrafts?.meta?.cursor_next
      : undefined
  });

  const handleRowClick = useHandleRowClick<BatchDraftsItem>(setSelectedRowData);

  const totalCount = useMemo(
    () => payoutDrafts?.meta?.total || 0,
    [payoutDrafts]
  );

  if (!batchId) return null;

  return (
    <DataTable
      total={totalCount}
      dataSource={data}
      columns={batchPayoutsColumns}
      onScroll={onScroll}
      onRowClick={handleRowClick}
      isLoading={isFetching}
      rowKey="id"
      selectedRow={selectedRowData}
      tableDataTestId={BATCH_PAYOUTS_DRAFTS_TABLE_TEST_ID}
      rowDataTestId="batch-draft-payouts-table-row-entry"
      rightPanel={
        selectedRowData ? (
          <BatchPayoutDraftDetails
            onClose={() => setSelectedRowData(undefined)}
            payoutId={selectedRowData.id.toString()}
            batchId={batchId}
          />
        ) : undefined
      }
    />
  );
};
